import { useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { BikeIcon, Search, DollarSign, Clock, Calendar as CalendarIcon } from "lucide-react";
import { getRentals } from "@/integrations/supabase/services";
import { useQuery } from "@tanstack/react-query";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useApiService } from "@/hooks/useApiService";
import { formatDateYearMonth } from "@/lib/utils";

const Rentals = () => {
  const { callService } = useApiService(); // Get callService
  const [search, setSearch] = useState("");
  
  const [startDate, setStartDate] = useState(() => {
    return new Date();
  });

  const { data: rentals = [], isLoading, error } = useQuery({
    queryKey: ['rentals', startDate],
    queryFn: () => callService(() => getRentals(startDate.toISOString().split('T')[0], 'eq')),
  });
  
  const filteredRentals = rentals.filter(
    (rental) =>
      rental.customer?.name.toLowerCase().includes(search.toLowerCase()) ||
      rental.customer?.phone.includes(search) ||
      rental.bike?.model.toLowerCase().includes(search.toLowerCase())
  );

  const groupedRentals = filteredRentals.reduce((acc, rental) => {
    const branchName = rental.branch?.name || "Sin Sucursal";
    if (!acc[branchName]) {
      acc[branchName] = [];
    }
    acc[branchName].push(rental);
    return acc;
  }, {} as Record<string, typeof filteredRentals>);

  const renderRentalRows = (rentals: typeof filteredRentals) => {
    const rows: JSX.Element[] = [];
    const monthlyTotals: Record<string, number> = {};
    const monthlyTotalRecords: Record<string, number> = {};

    rentals.forEach((rental) => {
      const formatted = formatDateYearMonth(rental.start_date);
      const currentMonth = formatted.charAt(0).toUpperCase() + formatted.slice(1);      
      monthlyTotals[currentMonth] = (monthlyTotals[currentMonth] || 0) + (rental.total_amount || 0);
      monthlyTotalRecords[currentMonth] = (monthlyTotalRecords[currentMonth] || 0) + 1;
    });

    let lastMonth = "";
    rentals.forEach((rental, index) => {
      const formatted = formatDateYearMonth(rental.start_date);
      const currentMonth = formatted.charAt(0).toUpperCase() + formatted.slice(1);

      if (lastMonth && currentMonth !== lastMonth) {
        rows.push(
          <TableRow key={`total-${lastMonth}`} className="bg-green-50 hover:bg-green-100">
            <TableCell colSpan={4} className="text-right font-bold p-2">Total {lastMonth}</TableCell>
            <TableCell className="font-bold p-2">
              <div className="flex items-center text-green-600 font-medium">
                <DollarSign className="h-4 w-4" />
                {monthlyTotals[lastMonth].toFixed(2)}
              </div>
            </TableCell>
            <TableCell colSpan={2} className="text-right font-bold p-2">Alquileres {monthlyTotalRecords[lastMonth]}</TableCell>
          </TableRow>
        );
      }

      rows.push(
        <TableRow key={rental.id}>
          <TableCell className="py-2">{rental.customer?.name}</TableCell>
          <TableCell className="py-2">{rental.customer?.phone}</TableCell>
          <TableCell className="py-2">{"#" + rental.bike?.number +" "+ rental.bike?.model +" "+ rental.bike?.size}</TableCell>
          <TableCell className="py-2">
            {rental.plan ? (
              <div className="flex flex-col">
                <span className="font-medium">{rental.plan.name}</span>
                {rental.duration_hours && (
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock className="h-3 w-3 mr-1" />
                    {rental.duration_hours}h
                  </div>
                )}
              </div>
            ) : (
              "—"
            )}
          </TableCell>
          <TableCell className="py-2">
            {rental.total_amount ? (
              <div className="flex items-center text-green-600 font-medium">
                <DollarSign className="h-4 w-4" />
                {rental.total_amount}
              </div>
            ) : (
              "—"
            )}
          </TableCell>
          <TableCell className="py-2">{format(new Date(rental.start_date), 'dd/MM hh:mm a')}</TableCell>
          <TableCell className="py-2">
            {rental.expected_return_time ?
              format(new Date(rental.expected_return_time), 'dd/MM hh:mm a') : "—"
            }
          </TableCell>
          <TableCell className="py-2">
            {rental.end_date ?
              format(new Date(rental.end_date), 'dd/MM hh:mm a') : "—"
            }
          </TableCell>
          <TableCell className="py-2">
            <Badge
              variant={rental.status === "active" ? "default" : "secondary"}
              className={
                rental.status === "active"
                  ? "bg-bike-500"
                  : ""
              }
            >
              {rental.status === "active" ? "Activo" : "Completado"}
            </Badge>
          </TableCell>
        </TableRow>
      );

      lastMonth = currentMonth;

      if (index === rentals.length - 1) {
        rows.push(
          <TableRow key={`total-${lastMonth}`} className="bg-green-50 hover:bg-green-100">
            <TableCell colSpan={4} className="text-right font-bold p-2">Total {lastMonth}</TableCell>
            <TableCell className="font-bold p-2">
              <div className="flex items-center text-green-600 font-medium">
                <DollarSign className="h-4 w-4" />
                {monthlyTotals[lastMonth].toFixed(2)}
              </div>
            </TableCell>
            <TableCell colSpan={2} className="text-right font-bold p-2">Alquileres {monthlyTotalRecords[lastMonth]}</TableCell>
            <TableCell colSpan={2}></TableCell>
          </TableRow>
        );
      }
    });

    return { rows, monthlyTotals };
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Alquileres</h1>
          <Link to="/new-rental">
            <Button>
              <BikeIcon className="mr-2 h-4 w-4" />
              Nuevo Alquiler
            </Button>
          </Link>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar alquileres..."
              className="pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="md:w-auto">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(startDate, "PPP", { locale: es })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => date && setStartDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {isLoading ? (
          <div className="text-center h-24">Cargando alquileres...</div>
        ) : error ? (
          <div className="text-center h-24 text-red-500">Error cargando los alquileres.</div>
        ) : Object.keys(groupedRentals).length === 0 ? (
          <div className="text-center h-24">No se han realizado alquileres aún.</div>
        ) : (
          <Accordion type="single" collapsible className="w-full space-y-4">
            {Object.entries(groupedRentals).map(([branchName, branchRentals]) => {
              const { rows, monthlyTotals } = renderRentalRows(branchRentals);
              const now = new Date();
              const currentMonthString = format(now, "MMM yyyy", { locale: es }).charAt(0).toUpperCase() + format(now, "MMM yyyy", { locale: es }).slice(1);
              const currentMonthTotal = monthlyTotals[currentMonthString] || 0;

              const activeRentalsCount = branchRentals.filter(r => r.status === 'active').length;

              return (
                <AccordionItem value={branchName} key={branchName} className="bg-white shadow-md rounded-lg">
                  <AccordionTrigger className="text-xl font-bold text-gray-800 px-4 py-3">
                    <div className="flex justify-between w-full">
                      <span>{branchName} ({activeRentalsCount})</span>
                      <span className="text-green-600 font-medium">
                        {currentMonthString}: ${currentMonthTotal.toFixed(2)}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-2">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-200 hover:bg-gray-200">
                          <TableHead>Cliente</TableHead>
                          <TableHead>Teléfono</TableHead>
                          <TableHead>Bici</TableHead>
                          <TableHead>Plan</TableHead>
                          <TableHead>Total</TableHead>
                          <TableHead>Alquiló</TableHead>
                          <TableHead>Debe Devolver</TableHead>
                          <TableHead>Devolvio</TableHead>
                          <TableHead>Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows}
                      </TableBody>
                    </Table>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>
    </MainLayout>
  );
};

export default Rentals;
